// World objects: tile entities and harvesting.
import { Facing, GameConstants, TileEntityType } from '../constants';
import { feetTile } from '../motor';

export interface TileEntityDefinition { type: TileEntityType; name: string; width: number; height: number; solid: boolean; verb: string }

const tileEntityDefs = new Map<TileEntityType, TileEntityDefinition>([
  [TileEntityType.Workbench, { type: TileEntityType.Workbench, name: 'Workbench', width: 1, height: 1, solid: false, verb: 'Craft' }],
  [TileEntityType.Npc, { type: TileEntityType.Npc, name: 'NPC', width: 1, height: 1, solid: true, verb: 'Talk' }],
  [TileEntityType.Portal, { type: TileEntityType.Portal, name: 'Portal', width: 1, height: 1, solid: false, verb: 'Travel' }],
  [TileEntityType.Furnace, { type: TileEntityType.Furnace, name: 'Furnace', width: 1, height: 1, solid: false, verb: 'Smelt' }],
]);

export const tileEntityDef = (t: TileEntityType) => tileEntityDefs.get(t);

/** A world object with a footprint and behaviour (stations, NPCs, portals). data: an NPC's id, a portal's flags. */
export interface TileEntity { id: number; type: TileEntityType; x: number; y: number; data: number; label: string }

export const TileEntities = {
  /** Portal data flag: usable, but no swirl is drawn. */
  portalHidden: 1,
  maxLabelLength: 40,
  reachPixels: 24,
  clientReachPixels: 20,

  isInteractionPoint: (e: TileEntity) => e.type === TileEntityType.Workbench || e.type === TileEntityType.Furnace,

  /** Invisible stations target the adjacent tile directly ahead, with fishing's point reach. */
  canInteract(e: TileEntity, x: number, y: number, facing: number, reach?: number): boolean {
    const point = TileEntities.isInteractionPoint(e);
    const limit = reach ?? (point ? Harvest.clientReachPixels : TileEntities.clientReachPixels);
    if (!point) return TileEntities.distanceTo(e, x, y) <= limit;
    const [fx, fy] = feetTile(x, y);
    const [dx, dy] = facing === Facing.Up ? [0, -1] : facing === Facing.Down ? [0, 1] : facing === Facing.Left ? [-1, 0] : [1, 0];
    return e.x === fx + dx && e.y === fy + dy && TileEntities.distanceTo(e, x, y) <= limit;
  },

  footprint(e: TileEntity): [number, number][] {
    const d = tileEntityDef(e.type)!;
    const out: [number, number][] = [];
    for (let y = e.y; y < e.y + d.height; y++) for (let x = e.x; x < e.x + d.width; x++) out.push([x, y]);
    return out;
  },

  /** Distance in pixels from a point to the nearest edge of the footprint (0 inside). */
  distanceTo(e: TileEntity, px: number, py: number): number {
    const d = tileEntityDef(e.type)!, ts = GameConstants.tileSize;
    if (TileEntities.isInteractionPoint(e)) {
      const [x, y] = TileEntities.center(e);
      return Math.hypot(px - x, py - y);
    }
    const cx = Math.min(Math.max(px, e.x * ts), (e.x + d.width) * ts);
    const cy = Math.min(Math.max(py, e.y * ts), (e.y + d.height) * ts);
    return Math.hypot(px - cx, py - cy);
  },

  center(e: TileEntity): [number, number] {
    const d = tileEntityDef(e.type)!, ts = GameConstants.tileSize;
    return [(e.x + d.width / 2) * ts, (e.y + d.height / 2) * ts];
  },
};

export const Harvest = {
  reachPixels: 28,
  /** Slightly shorter than the server's, so the client never asks for something it will refuse. */
  clientReachPixels: 25,

  tileCenter: (tx: number, ty: number): [number, number] => [(tx + 0.5) * GameConstants.tileSize, (ty + 0.5) * GameConstants.tileSize],

  /** Point just in front of the player, used to rank interaction targets. */
  aimPoint(x: number, y: number, facing: Facing): [number, number] {
    const [dx, dy] = facing === Facing.Up ? [0, -1] : facing === Facing.Down ? [0, 1] : facing === Facing.Left ? [-1, 0] : [1, 0];
    return [x + dx * 10, y - 4 + dy * 10];
  },

};

/** Mobs: attacks aim at a point a little above a mob's feet. */
export const MobRules = { hitCenterOffsetY: -4 };
